/**
 * A step-up prompt the client draws itself — the phone (Settings → Security, D4).
 *
 * `useStepUp` wraps Clerk's `useReverification`. On the web Clerk draws its own
 * re-verification modal. `@clerk/clerk-expo` has no UI on a phone, so there a
 * sensitive call (add an email, connect an account, set a password) would wait
 * for a modal that never opens. A client that draws its own prompt mounts
 * `StepUpPromptProvider` with `useStepUpPrompt().onNeedsReverification`; every
 * `useStepUp` below it hands Clerk that handler instead of the modal.
 *
 * The prompt is Clerk's session verification, proving it is the person with
 * what the account has:
 *
 *   start          `session.startVerification({ level })` — which factors prove it
 *   password       `attemptFirstFactorVerification({ strategy: 'password' })`
 *   email code     `prepareFirstFactorVerification({ strategy: 'email_code' })` to
 *                  the sign-in email, then `attemptFirstFactorVerification(…code)`
 *                  — the way for an account with no password, or by choice
 *   authenticator  `attemptSecondFactorVerification({ strategy: 'totp' })`, when
 *                  the account has one and the level asks for it
 *
 * Complete: Clerk retries the sensitive call. Dismissed: Clerk rejects it as
 * cancelled, which `useStepUp` reports as `cancelled` — not an error. Nothing
 * here is put into words: a refusal is Clerk's error, for the screen to say
 * (`useAuthErrorMessage`).
 */
import { useSession } from '@clerk/clerk-react';
import type { SessionVerificationLevel, SessionVerificationResource } from '@clerk/shared/types';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

/** What Clerk hands a custom prompt when a call needs re-verification. */
export interface NeedsReverification {
  level: SessionVerificationLevel | undefined;
  complete: () => void;
  cancel: () => void;
}

export type StepUpPromptHandler = (request: NeedsReverification) => void;

const StepUpPromptContext = createContext<StepUpPromptHandler | null>(null);

/** Below it, every `useStepUp` asks through `handler` instead of Clerk's own modal. */
export function StepUpPromptProvider({
  handler,
  children,
}: {
  handler: StepUpPromptHandler;
  children: ReactNode;
}) {
  return <StepUpPromptContext.Provider value={handler}>{children}</StepUpPromptContext.Provider>;
}

/** The prompt `useStepUp` should hand Clerk, or null for Clerk's own modal (web). */
export function useStepUpPromptHandler(): StepUpPromptHandler | null {
  return useContext(StepUpPromptContext);
}

export type StepUpPromptStep =
  | { step: 'idle' }
  /** Asking Clerk which factors prove it is the person. */
  | { step: 'starting' }
  /** The account's password — or a code to the email instead, when `codeAvailable`. */
  | { step: 'password'; codeAvailable: boolean }
  /** A 6-digit code sent to the sign-in email (`email`, as Clerk masks it). */
  | { step: 'code'; email: string }
  /** The authenticator app's code (a second factor the account has). */
  | { step: 'totp' }
  /** Nothing the platform offers can prove it here (`error` says why); only closing is left. */
  | { step: 'unavailable'; error: unknown };

interface EmailFactor {
  emailAddressId: string;
  safeIdentifier: string;
}

export function useStepUpPrompt() {
  const { session } = useSession();
  const pending = useRef<NeedsReverification | null>(null);
  const emailFactor = useRef<EmailFactor | null>(null);
  const [state, setState] = useState<StepUpPromptStep>({ step: 'idle' });
  const [busy, setBusy] = useState(false);
  /** Clerk's refusal of the last attempt (a wrong password or code); null when none. */
  const [error, setError] = useState<unknown>(null);

  const reset = useCallback(() => {
    pending.current = null;
    emailFactor.current = null;
    setBusy(false);
    setError(null);
    setState({ step: 'idle' });
  }, []);

  const finish = useCallback(() => {
    const request = pending.current;
    reset();
    request?.complete();
  }, [reset]);

  const cancel = useCallback(() => {
    const request = pending.current;
    reset();
    request?.cancel();
  }, [reset]);

  const sendCode = useCallback(async (): Promise<boolean> => {
    const factor = emailFactor.current;
    if (!session || !factor) return false;
    await session.prepareFirstFactorVerification({
      strategy: 'email_code',
      emailAddressId: factor.emailAddressId,
    });
    setState({ step: 'code', email: factor.safeIdentifier });
    return true;
  }, [session]);

  /** Where a verification stands now: done, a second factor to ask, or still the first. */
  const settle = useCallback(
    (verification: SessionVerificationResource) => {
      switch (verification.status) {
        case 'complete':
          finish();
          return;
        case 'needs_second_factor':
          if (verification.supportedSecondFactors?.some((f) => f.strategy === 'totp')) {
            setState({ step: 'totp' });
          } else {
            setState({
              step: 'unavailable',
              error: new Error('Step-up needs a second factor the platform does not offer'),
            });
          }
          return;
        case 'needs_first_factor':
          return;
        default: {
          const exhaustive: never = verification.status;
          throw new Error(`Unhandled verification status: ${String(exhaustive)}`);
        }
      }
    },
    [finish]
  );

  const begin = useCallback(
    async (level: SessionVerificationLevel | undefined) => {
      if (!session) {
        setState({ step: 'unavailable', error: new Error('No session to verify') });
        return;
      }
      try {
        const verification = await session.startVerification({ level: level ?? 'first_factor' });
        if (verification.status !== 'needs_first_factor') {
          settle(verification);
          return;
        }
        const factors = verification.supportedFirstFactors ?? [];
        const email = factors.find((f) => f.strategy === 'email_code');
        emailFactor.current =
          email && 'emailAddressId' in email
            ? { emailAddressId: email.emailAddressId, safeIdentifier: email.safeIdentifier }
            : null;
        if (factors.some((f) => f.strategy === 'password')) {
          setState({ step: 'password', codeAvailable: emailFactor.current !== null });
          return;
        }
        if (emailFactor.current) {
          await sendCode();
          return;
        }
        setState({
          step: 'unavailable',
          error: new Error('Step-up offers no factor the platform supports'),
        });
      } catch (err: unknown) {
        setState({ step: 'unavailable', error: err });
      }
    },
    [session, settle, sendCode]
  );

  /** The handler `StepUpPromptProvider` hands to every `useStepUp`. */
  const onNeedsReverification = useCallback<StepUpPromptHandler>(
    (request) => {
      // A second sensitive call while one is asking: the first is dismissed.
      pending.current?.cancel();
      pending.current = request;
      emailFactor.current = null;
      setError(null);
      setBusy(false);
      setState({ step: 'starting' });
      void begin(request.level);
    },
    [begin]
  );

  /** Run one attempt: busy while it runs, Clerk's refusal kept for the screen. */
  const attempt = useCallback(
    async (run: () => Promise<SessionVerificationResource | void>) => {
      setBusy(true);
      setError(null);
      try {
        const verification = await run();
        if (verification) settle(verification);
      } catch (err: unknown) {
        setError(err);
      } finally {
        setBusy(false);
      }
    },
    [settle]
  );

  const submitPassword = useCallback(
    (password: string) =>
      attempt(async () =>
        session?.attemptFirstFactorVerification({ strategy: 'password', password })
      ),
    [attempt, session]
  );

  const submitCode = useCallback(
    (code: string) =>
      attempt(async () =>
        session?.attemptFirstFactorVerification({ strategy: 'email_code', code })
      ),
    [attempt, session]
  );

  const submitTotp = useCallback(
    (code: string) =>
      attempt(async () => session?.attemptSecondFactorVerification({ strategy: 'totp', code })),
    [attempt, session]
  );

  /** A code to the email instead of the password — or a new one. Resolves `true` when sent. */
  const requestCode = useCallback(async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      return await sendCode();
    } catch (err: unknown) {
      setError(err);
      return false;
    } finally {
      setBusy(false);
    }
  }, [sendCode]);

  return {
    state,
    busy,
    error,
    onNeedsReverification,
    submitPassword,
    submitCode,
    submitTotp,
    requestCode,
    cancel,
  };
}
